import { useState, useEffect } from "react";
import imgLogo from "@/assets/ig-logo-2.png";
import useNavigateWithParams from "@/hooks/use-utm-params";
import { useProfileCompleteQuery } from "@/hooks/use-profile-query";
import { storage } from "@/services/storage";

export function DeliverySearchPage() {
  const [username, setUsername] = useState("");
  const [isError, setIsError] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [searchStarted, setSearchStarted] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  const navigate = useNavigateWithParams();

  // Hook para buscar dados do perfil - só ativado quando searchStarted é true
  const {
    data,
    isLoading,
    isError: fetchError,
    progress,
    hasSearchedBefore,
  } = useProfileCompleteQuery(searchStarted ? username : "", "delivery");

  // Verifica se já tem dados em cache
  useEffect(() => {
    if (hasSearchedBefore && !searchStarted) {
      const cachedData = storage.getProfileData();
      if (cachedData && cachedData.profileData) {
        // Preenche o campo com o username do cache no carregamento inicial
        setUsername(cachedData.profileData.username);
      }
    }
  }, [hasSearchedBefore, searchStarted]);

  // Efeito para marcar quando os dados estão prontos para redirecionamento
  useEffect(() => {
    if (data && searchStarted && !isLoading) {
      // Apenas marca que devemos redirecionar, não redireciona imediatamente
      setShouldRedirect(true);
    }
  }, [data, isLoading, searchStarted]);

  // Efeito separado para o redirecionamento real
  useEffect(() => {
    if (shouldRedirect) {
      navigate("/delivery/profile", { username });
    }
  }, [shouldRedirect, username, navigate]);

  // Função para lidar com o envio do formulário
  const handleSubmit = () => {
    if (!username.trim()) {
      setIsError(true);
      return;
    }

    setIsError(false);

    // Verificar se o username atual é diferente do que está em cache
    const cachedData = storage.getProfileData();
    if (cachedData && cachedData.profileData.username !== username.trim()) {
      // Se for diferente, limpar os dados em cache para forçar uma nova busca
      storage.clearProfileData();
    }

    // Apenas iniciar a busca se ainda não começou
    if (!searchStarted) {
      setSearchStarted(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Garantir que não estamos em busca ativa
    if (!searchStarted) {
      setUsername(e.target.value);
      if (isTouched) {
        setIsError(!e.target.value.trim());
      }
    }
  };

  const handleBlur = () => {
    setIsTouched(true);
    setIsError(!username.trim());
  };

  const handleTryAgain = () => {
    // Limpar tudo para uma nova tentativa
    setSearchStarted(false);
    setShouldRedirect(false);
    // Limpar cache se o usuário decidiu tentar novamente após um erro
    storage.clearProfileData();
  };

  return (
    <div className="min-h-screen flex flex-col bg-neutral-900 text-neutral-100">
      <div className="container max-w-[450px] mx-auto px-6 py-8">
        <header className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img src={imgLogo} alt="Logo" className="w-[60px]" />
          </div>

          <h1 className="font-bold text-center mt-8 text-3xl text-white">
            Bienvenido(a)
          </h1>

          <p className="text-center w-full font-normal text-neutral-400 text-lg mt-3">
            Para continuar, necesitamos más información:
          </p>
        </header>

        {searchStarted && isLoading ? (
          <div className="bg-neutral-800 rounded-xl border border-neutral-700 p-6 mt-6">
            <div className="flex flex-col items-center justify-center">
              <div className="relative w-20 h-20 mb-6">
                <svg
                  className="animate-spin w-20 h-20 text-orange-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {Math.round(progress)}%
                  </span>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-white mb-2">
                Buscando información
              </h3>
              <p className="text-sm text-neutral-400 text-center mb-4">
                Estamos procesando tu cuenta de Instagram para preparar tu
                acceso.
              </p>

              <div className="w-full bg-neutral-700 rounded-full h-2.5 mb-4">
                <div
                  className="bg-gradient-to-r from-orange-500 to-red-500 h-2.5 rounded-full"
                  style={{ width: `${Math.max(5, progress)}%` }}
                ></div>
              </div>

              <p className="text-xs text-neutral-500">
                Esto puede tomar algunos segundos. Por favor, espera...
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-neutral-800 rounded-xl border border-neutral-700 p-6 mt-6">
            {fetchError && searchStarted ? (
              <div className="text-center py-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-12 h-12 text-red-500 mx-auto mb-3"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                  />
                </svg>

                <h3 className="text-lg font-semibold text-white mb-2">
                  No fue posible encontrar el perfil
                </h3>
                <p className="text-sm text-neutral-400 mb-4">
                  Verifica si el nombre de usuario es correcto e intenta
                  nuevamente.
                </p>
                <button
                  onClick={handleTryAgain}
                  className="px-4 py-2 rounded-lg font-medium text-white bg-neutral-700 hover:bg-neutral-600 transition-colors cursor-pointer"
                >
                  Intentar nuevamente
                </button>
              </div>
            ) : (
              <div className="grid w-full items-center gap-4">
                <div>
                  <label
                    htmlFor="username"
                    className="block text-neutral-300 text-base font-medium mb-2"
                  >
                    Tu Instagram{" "}
                    <span className="text-neutral-400">(sin el @)</span>
                  </label>
                  <div className="relative">
                    <input
                      id="username"
                      placeholder="username"
                      name="username"
                      value={username}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      disabled={searchStarted}
                      className={`w-full px-4 py-3 rounded-lg border bg-neutral-700 text-white placeholder-neutral-400 ${
                        isError
                          ? "border-red-500 focus:ring-red-500"
                          : "border-neutral-600 focus:ring-orange-500"
                      } focus:border-transparent focus:outline-none focus:ring-2 ${
                        searchStarted ? "opacity-70" : ""
                      }`}
                    />
                  </div>

                  {isError && (
                    <p className="mt-1 text-sm text-red-400">
                      Por favor, ingresa tu nombre de usuario de Instagram.
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  disabled={searchStarted}
                  className={`w-full py-3 mt-2 rounded-lg font-semibold text-white transition-all focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 focus:outline-none focus:ring-offset-neutral-800 text-base cursor-pointer ${
                    searchStarted ? "opacity-70" : "hover:opacity-90"
                  }`}
                  style={{
                    backgroundImage:
                      "linear-gradient(90deg, rgba(237, 127, 0, 1), rgba(255, 90, 53, 1))",
                  }}
                  onClick={handleSubmit}
                >
                  Continuar
                </button>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-xs text-neutral-400">
            Tus datos están protegidos de acuerdo con nuestra{" "}
            <a href="#" className="text-orange-400 hover:underline">
              Política de Privacidad
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
